import { useMemo, useState } from 'react'
import { navigate } from './App.jsx'

// La page d'accueil est une suite de sections, chacune découpée en colonnes.
// La collectivité compose : une section pleine largeur pour le logo et le mot
// du maire, une section en trois colonnes pour les actualités, et ainsi de suite.
export default function Home({ site }) {
  const [q, setQ] = useState('')
  const sections = (site.blocks || []).filter((s) => s.on !== false)
  const tous = sections.flatMap((s) => (s.colonnes || []).flat()).filter(Boolean)
  const hero = tous.find((b) => b.type === 'hero' && b.on !== false)

  const trouves = useMemo(() => {
    if (!q.trim()) return null
    const n = q.toLowerCase()
    return site.forms.filter((f) =>
      (f.title + ' ' + f.description + ' ' + f.category).toLowerCase().includes(n))
  }, [q, site.forms])

  const parCategorie = useMemo(() => {
    const out = {}
    for (const f of site.forms) (out[f.category] ||= []).push(f)
    return out
  }, [site.forms])

  const contexte = { site, q, setQ, trouves, parCategorie }

  return (
    <>
      {hero && (
        <section className={`hero ${site.theme.banner || 'aplat'}`}>
          <div className="in">
            <h1>{hero.title}</h1>
            <p>{hero.lede}</p>
          </div>
        </section>
      )}

      {sections.map((section) => {
        const colonnes = (section.colonnes || [])
          .map((col) => (col || []).filter((b) => b?.on !== false && b.type !== 'hero'))
        if (!colonnes.some((c) => c.length)) return null
        return (
          <section className={`section fond-${section.fond || 'blanc'}`} key={section.id}>
            <div className="in">
              <div className={`colonnes col-${section.layout || '1'}`}>
                {colonnes.map((col, i) => (
                  <div className="colonne" key={i}>
                    {col.map((b) => <Bloc key={b.id} b={b} {...contexte} />)}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )
      })}
    </>
  )
}

function Bloc({ b, site, q, setQ, trouves, parCategorie }) {
  switch (b.type) {
    case 'alerte':
      return <div className="alerte" role="status">{b.text}</div>

    case 'search':
      return (
        <>
          <div className="recherche">
            <label htmlFor="q" className="sr">Rechercher une démarche</label>
            <input id="q" type="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Que cherchez-vous ? acte, école, encombrants…" />
          </div>
          {trouves && (
            <>
              <h2>{trouves.length} résultat{trouves.length > 1 ? 's' : ''}</h2>
              <Cartes forms={trouves} />
            </>
          )}
        </>
      )

    case 'popular': {
      if (trouves) return null
      const mis = site.forms.filter((f) => f.featured).slice(0, b.n || 6)
      const liste = mis.length ? mis : site.forms.slice(0, b.n || 6)
      if (!liste.length) return null
      return (
        <>
          <h2>{b.title || 'Les plus demandées'}</h2>
          <div className="raccourcis">
            {liste.map((f) => (
              <button key={f.slug} onClick={() => navigate(`/demarche/${f.slug}`)}>{f.title}</button>
            ))}
          </div>
        </>
      )
    }

    case 'categories':
      if (trouves) return null
      return (
        <>
          <h2>{b.title || 'Toutes les démarches'}</h2>
          {Object.entries(parCategorie).map(([cat, forms]) => (
            <div key={cat} className="categorie">
              <h3>{cat}</h3>
              <Cartes forms={forms} />
            </div>
          ))}
        </>
      )

    case 'suivi':
      return (
        <div className="encart">
          <div>
            <h2>Une demande déjà déposée ?</h2>
            <p>Retrouvez son avancement, les messages du service et les pièces à fournir.</p>
          </div>
          <button className="btn pri" onClick={() => navigate('/suivi')}>Suivre ma demande</button>
        </div>
      )

    case 'texte':
      return (
        <div className="bloc-texte">
          {b.title && <h2>{b.title}</h2>}
          {(b.text || '').split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )

    case 'image': {
      if (!b.url) return null
      const image = <img src={b.url} alt={b.alt || ''} loading="lazy"
                         className={`illustration ${b.width || 'large'}`} />
      return (
        <figure className="bloc-image">
          {b.link ? <a href={b.link}>{image}</a> : image}
          {b.caption && <figcaption>{b.caption}</figcaption>}
        </figure>
      )
    }

    case 'logo':
      if (!site.theme.logo_url) return null
      return (
        <div className={`bloc-logo align-${b.align || 'centre'}`}>
          <img src={site.theme.logo_url} alt={site.org.name}
               className={b.width || 'moyenne'} loading="lazy" />
        </div>
      )

    case 'liens':
      return (
        <>
          <h2>{b.title || 'Liens utiles'}</h2>
          <ul className="bloc-liens">
            {(b.items || []).filter((l) => l.url && l.label).map((l, i) => (
              <li key={i}>
                <a href={l.url}>{l.label}</a>
                {l.hint && <span>{l.hint}</span>}
              </li>
            ))}
          </ul>
        </>
      )

    case 'chiffres':
      return (
        <>
          {b.title && <h2>{b.title}</h2>}
          <div className="bloc-chiffres">
            {(b.items || []).map((c, i) => (
              <div key={i}><strong>{c.value}</strong><span>{c.label}</span></div>
            ))}
          </div>
        </>
      )

    case 'news':
      return (
        <>
          <h2>{b.title || 'Actualités'}</h2>
          <div className="pnews">
            {(b.items || []).map((it, i) => (
              <div className="it" key={i}><b>{it.t}</b><span>{it.d}</span></div>
            ))}
          </div>
        </>
      )

    case 'infos':
      return (
        <div className="infos">
          <div><h3>Horaires</h3><p>{site.contact.horaires}</p></div>
          <div><h3>Nous joindre</h3><p>{site.contact.tel}<br />{site.contact.mail}</p></div>
          <div><h3>Adresse</h3><p>{site.contact.adresse}</p></div>
        </div>
      )

    default:
      return null
  }
}

function Cartes({ forms }) {
  return (
    <div className="cartes">
      {forms.map((f) => (
        <article className="carte" key={f.slug}>
          <p className="cat">{f.category}</p>
          <h3><a href={`/demarche/${f.slug}`}
                 onClick={(e) => { e.preventDefault(); navigate(`/demarche/${f.slug}`) }}>
            {f.title}
          </a></h3>
          <p>{f.description}</p>
          <span className="go" aria-hidden="true">Commencer →</span>
        </article>
      ))}
    </div>
  )
}
